import { I18nService } from '../../i18n/I18nService';
import { I18N_KEYS } from '../../i18n/keys';
import { FocusTrap } from '../../accessibility/FocusTrap';
import { GameSettings, UILocale } from '../../types';
import { LanguageInfo, StyleInfo } from '../../types/manifest';
import { validateRange } from '../../utils/validators';
import { MIN_AUTO_PLAY_INTERVAL, MAX_AUTO_PLAY_INTERVAL } from '../../constants/defaults';

export class SettingsModal {
  private i18n: I18nService;
  private focusTrap: FocusTrap;
  private dialog: HTMLDialogElement | null = null;
  private isGamePlaying: boolean = false;

  onApply: ((settings: GameSettings) => void) | null = null;
  onClose: (() => void) | null = null;
  onPreview: ((lang: string, style: string) => void) | null = null;
  onGetStylesForLanguage: ((lang: string) => StyleInfo[]) | null = null;
  onGetFileCount: ((lang: string, style: string) => number) | null = null;
  
  setPreviewButtonState(isPlaying: boolean): void {
    const btn = this.dialog?.querySelector('#settings-preview-btn') as HTMLButtonElement;
    if (btn) {
      if (isPlaying) {
        btn.textContent = `⏸️ ${this.i18n.t(I18N_KEYS.SETTINGS_PREVIEW)}`;
        btn.disabled = true;
        btn.classList.add('settings__preview-btn--playing');
      } else {
        btn.textContent = `🔊 ${this.i18n.t(I18N_KEYS.SETTINGS_PREVIEW)}`;
        btn.disabled = false;
        btn.classList.remove('settings__preview-btn--playing');
      }
    }
  }

  constructor(i18n: I18nService, focusTrap: FocusTrap) {
    this.i18n = i18n;
    this.focusTrap = focusTrap;
  }

  render(): void {
    this.dialog = document.getElementById('settings-modal') as HTMLDialogElement;
    if (!this.dialog) {
      this.dialog = document.createElement('dialog');
      this.dialog.id = 'settings-modal';
      this.dialog.className = 'modal';
      document.body.appendChild(this.dialog);
    }
  }

  open(
    currentSettings: GameSettings,
    languages: LanguageInfo[],
    styles: StyleInfo[],
    isPlaying: boolean,
    maxFiles: number
  ): void {
    if (!this.dialog) this.render();
    this.isGamePlaying = isPlaying;

    const localeOptions = this.i18n.getSupportedLocales();
    const rangeLocked = isPlaying;

    this.dialog!.innerHTML = `
      <div class="modal__content">
        <div class="modal__header">
          <h2>${this.i18n.t(I18N_KEYS.SETTINGS_TITLE)}</h2>
          <button class="modal__close-btn" type="button" aria-label="Close">&times;</button>
        </div>
        <div class="modal__body">
          <div class="settings__field">
            <label for="settings-ui-lang">${this.i18n.t(I18N_KEYS.SETTINGS_UI_LANGUAGE)}</label>
            <select id="settings-ui-lang">
              ${localeOptions.map(l => `<option value="${l.code}" ${l.code === currentSettings.uiLocale ? 'selected' : ''}>${l.nativeName}</option>`).join('')}
            </select>
          </div>
          <div class="settings__field">
            <label for="settings-lang">${this.i18n.t(I18N_KEYS.SETTINGS_LANGUAGE)}</label>
            <select id="settings-lang">
              ${languages.map(l => `<option value="${l.code}" ${l.code === currentSettings.language ? 'selected' : ''}>${l.nativeName}</option>`).join('')}
            </select>
          </div>
          <div class="settings__field">
            <label for="settings-style">${this.i18n.t(I18N_KEYS.SETTINGS_STYLE)}</label>
            <div class="settings__style-row">
              <select id="settings-style">
                ${styles.map(s => `<option value="${s.key}" ${s.key === currentSettings.style ? 'selected' : ''}>${s.name}</option>`).join('')}
              </select>
              <button id="settings-preview-btn" class="settings__preview-btn" type="button">🔊 ${this.i18n.t(I18N_KEYS.SETTINGS_PREVIEW)}</button>
            </div>
            ${isPlaying ? `<p class="settings__warning">${this.i18n.t(I18N_KEYS.SETTINGS_HOT_SWITCH_WARNING)}</p>` : ''}
          </div>
          <div class="settings__field">
            <label>${this.i18n.t(I18N_KEYS.SETTINGS_RANGE)}</label>
            ${rangeLocked ? `<p class="settings__info">${this.i18n.t(I18N_KEYS.SETTINGS_RANGE_LOCKED)}</p>` : ''}
            <div class="settings__range-row">
              <label for="settings-min">${this.i18n.t(I18N_KEYS.SETTINGS_MIN)}</label>
              <input type="number" id="settings-min" value="${currentSettings.min}" min="1" max="${maxFiles}" ${rangeLocked ? 'disabled' : ''}>
              <label for="settings-max">${this.i18n.t(I18N_KEYS.SETTINGS_MAX)}</label>
              <input type="number" id="settings-max" value="${currentSettings.max}" min="1" max="${maxFiles}" ${rangeLocked ? 'disabled' : ''}>
            </div>
            <p id="settings-range-error" class="settings__error" style="display:none;"></p>
          </div>
          <div class="settings__field">
            <label for="settings-interval">${this.i18n.t(I18N_KEYS.SETTINGS_INTERVAL)}</label>
            <input type="range" id="settings-interval" min="${MIN_AUTO_PLAY_INTERVAL}" max="${MAX_AUTO_PLAY_INTERVAL}" value="${currentSettings.autoPlayInterval}">
            <span id="settings-interval-value">${currentSettings.autoPlayInterval}s</span>
          </div>
        </div>
        <div class="modal__footer">
          <button id="settings-cancel-btn" class="btn btn--secondary" type="button">${this.i18n.t(I18N_KEYS.SETTINGS_CANCEL)}</button>
          <button id="settings-apply-btn" class="btn btn--primary" type="button">${this.i18n.t(I18N_KEYS.SETTINGS_APPLY)}</button>
        </div>
      </div>
    `;

    // Bind events
    this.dialog!.querySelector('.modal__close-btn')?.addEventListener('click', () => this.close());
    this.dialog!.querySelector('#settings-cancel-btn')?.addEventListener('click', () => this.close());
    this.dialog!.querySelector('#settings-apply-btn')?.addEventListener('click', () => this.apply(maxFiles));
    this.dialog!.querySelector('#settings-preview-btn')?.addEventListener('click', () => {
      console.log('[SettingsModal] Preview button clicked');
      const lang = (this.dialog!.querySelector('#settings-lang') as HTMLSelectElement).value;
      const style = (this.dialog!.querySelector('#settings-style') as HTMLSelectElement).value;
      console.log('[SettingsModal] Language:', lang, 'Style:', style);
      console.log('[SettingsModal] onPreview callback exists:', !!this.onPreview);
      if (this.onPreview) {
        console.log('[SettingsModal] Calling onPreview callback');
        this.onPreview(lang, style);
      } else {
        console.warn('[SettingsModal] onPreview callback is not set!');
      }
    });

    // Language change handler - update styles dropdown
    const langSelect = this.dialog!.querySelector('#settings-lang') as HTMLSelectElement;
    const styleSelect = this.dialog!.querySelector('#settings-style') as HTMLSelectElement;
    const minInput = this.dialog!.querySelector('#settings-min') as HTMLInputElement;
    const maxInput = this.dialog!.querySelector('#settings-max') as HTMLInputElement;
    
    langSelect?.addEventListener('change', () => {
      const selectedLang = langSelect.value;
      if (this.onGetStylesForLanguage) {
        const newStyles = this.onGetStylesForLanguage(selectedLang);
        
        // Update style dropdown
        const currentStyle = styleSelect.value;
        const styleExists = newStyles.some(s => s.key === currentStyle);
        
        styleSelect.innerHTML = newStyles.map(s => 
          `<option value="${s.key}">${s.name}</option>`
        ).join('');
        
        // If current style doesn't exist in new language, select first style
        if (!styleExists && newStyles.length > 0) {
          styleSelect.value = newStyles[0].key;
        } else if (styleExists) {
          styleSelect.value = currentStyle;
        }
        
        // Update maxFiles and max range input
        const selectedStyle = styleSelect.value;
        if (this.onGetFileCount) {
          const newMaxFiles = this.onGetFileCount(selectedLang, selectedStyle);
          maxInput.max = String(newMaxFiles);
          if (parseInt(maxInput.value, 10) > newMaxFiles) {
            maxInput.value = String(newMaxFiles);
          }
          // Re-validate range
          this.validateRangeInputs(minInput, maxInput, newMaxFiles);
        }
      }
    });
    
    // Style change handler - update maxFiles
    styleSelect?.addEventListener('change', () => {
      const selectedLang = langSelect.value;
      const selectedStyle = styleSelect.value;
      if (this.onGetFileCount) {
        const newMaxFiles = this.onGetFileCount(selectedLang, selectedStyle);
        maxInput.max = String(newMaxFiles);
        if (parseInt(maxInput.value, 10) > newMaxFiles) {
          maxInput.value = String(newMaxFiles);
        }
        // Re-validate range
        this.validateRangeInputs(minInput, maxInput, newMaxFiles);
      }
    });

    // Range validation
    const validateInputs = () => {
      const currentLang = langSelect.value;
      const currentStyle = styleSelect.value;
      const currentMaxFiles = this.onGetFileCount ? this.onGetFileCount(currentLang, currentStyle) : maxFiles;
      this.validateRangeInputs(minInput, maxInput, currentMaxFiles);
    };
    minInput?.addEventListener('input', validateInputs);
    maxInput?.addEventListener('input', validateInputs);

    // Interval display
    const intervalSlider = this.dialog!.querySelector('#settings-interval') as HTMLInputElement;
    const intervalValue = this.dialog!.querySelector('#settings-interval-value') as HTMLElement;
    intervalSlider?.addEventListener('input', () => {
      intervalValue.textContent = `${intervalSlider.value}s`;
    });

    this.dialog!.showModal();
    this.focusTrap.activate(this.dialog!);
  }

  close(): void {
    this.focusTrap.deactivate();
    if (this.dialog?.open) {
      this.dialog.close();
    }
    if (this.onClose) this.onClose();
  }

  isOpen(): boolean {
    return this.dialog?.open === true;
  }

  private apply(maxFiles: number): void {
    if (!this.dialog) return;

    const uiLocale = (this.dialog.querySelector('#settings-ui-lang') as HTMLSelectElement).value as UILocale;
    const language = (this.dialog.querySelector('#settings-lang') as HTMLSelectElement).value;
    const style = (this.dialog.querySelector('#settings-style') as HTMLSelectElement).value;
    const min = parseInt((this.dialog.querySelector('#settings-min') as HTMLInputElement).value, 10);
    const max = parseInt((this.dialog.querySelector('#settings-max') as HTMLInputElement).value, 10);
    const interval = parseInt((this.dialog.querySelector('#settings-interval') as HTMLInputElement).value, 10);
    const autoPlayEnabled = false; // Preserve current, handled separately

    // Get current maxFiles based on selected language and style
    const currentMaxFiles = this.onGetFileCount ? this.onGetFileCount(language, style) : maxFiles;

    // Validate range
    const rangeResult = validateRange(min, max, currentMaxFiles);
    if (!rangeResult.valid) {
      const errorEl = this.dialog.querySelector('#settings-range-error') as HTMLElement;
      if (errorEl) {
        errorEl.textContent = rangeResult.error || '';
        errorEl.style.display = 'block';
      }
      return;
    }

    const settings: GameSettings = {
      language,
      style,
      min: this.isGamePlaying ? (this.dialog.querySelector('#settings-min') as HTMLInputElement).defaultValue as unknown as number : min,
      max: this.isGamePlaying ? (this.dialog.querySelector('#settings-max') as HTMLInputElement).defaultValue as unknown as number : max,
      autoPlayEnabled,
      autoPlayInterval: interval,
      uiLocale,
    };

    // If playing, preserve original range
    if (this.isGamePlaying) {
      // Keep the original min/max (they were disabled)
      settings.min = parseInt((this.dialog.querySelector('#settings-min') as HTMLInputElement).value, 10);
      settings.max = parseInt((this.dialog.querySelector('#settings-max') as HTMLInputElement).value, 10);
    }

    this.close();
    if (this.onApply) this.onApply(settings);
  }

  private validateRangeInputs(minInput: HTMLInputElement, maxInput: HTMLInputElement, maxFiles: number): void {
    const min = parseInt(minInput.value, 10);
    const max = parseInt(maxInput.value, 10);
    const result = validateRange(min, max, maxFiles);

    const errorEl = this.dialog?.querySelector('#settings-range-error') as HTMLElement;
    const applyBtn = this.dialog?.querySelector('#settings-apply-btn') as HTMLButtonElement;

    if (!result.valid) {
      if (errorEl) {
        errorEl.textContent = result.error || '';
        errorEl.style.display = 'block';
      }
      if (applyBtn) applyBtn.disabled = true;
    } else {
      if (errorEl) errorEl.style.display = 'none';
      if (applyBtn) applyBtn.disabled = false;
    }
  }
}
